import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Copy, Check, MessageSquare, Mail, Facebook, Instagram, Share2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { getAnnouncementShareUrl } from '@/lib/shareUrl';
import { buildShareLinks } from '@/lib/buildShareLinks';
import { getTemplateConfig } from '@/types/templates';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { useEventTracking } from '@/hooks/useEventTracking';

/**
 * TikTok logo (lucide-react has no built-in icon).
 */
function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V7.83a8.16 8.16 0 0 0 4.77 1.52V5.91a4.85 4.85 0 0 1-1.84-.22Z" />
    </svg>
  );
}

const copy = {
  en: {
    title: 'Share This Living Announcement',
    subtitle: 'A small share can change someone\'s day.',
    cardTagline: 'A heartfelt living announcement',
    forLabel: 'For',
    copyLink: 'Copy Link',
    text: 'Text',
    email: 'Email',
    facebook: 'Facebook',
    instagram: 'Instagram',
    tiktok: 'TikTok',
    nativeShare: 'Share…',
    copied: 'Link copied. Paste it wherever feels right.',
    copyFail: 'Couldn\'t copy. Long-press the link to copy.',
    igHint: 'Link copied — open Instagram and paste it in a story or DM.',
    ttHint: 'Link copied — open TikTok and paste it in a video caption or DM.',
    smsBody: 'Someone wrote a living announcement worth reading 💛',
    emailSubject: 'A living announcement for you',
    emailBody: 'I came across this — it felt worth sharing with you.',
    footer: 'You\'re in control of where this is shared.',
    brand: 'Say It NOW',
  },
  es: {
    title: 'Comparte este Anuncio Vivo',
    subtitle: 'Un pequeño gesto puede cambiar el día de alguien.',
    cardTagline: 'Un anuncio vivo desde el corazón',
    forLabel: 'Para',
    copyLink: 'Copiar enlace',
    text: 'Mensaje',
    email: 'Email',
    facebook: 'Facebook',
    instagram: 'Instagram',
    tiktok: 'TikTok',
    nativeShare: 'Compartir…',
    copied: 'Enlace copiado. Pégalo donde te parezca bien.',
    copyFail: 'No se pudo copiar. Mantén pulsado el enlace.',
    igHint: 'Enlace copiado — abre Instagram y pégalo en una historia o DM.',
    ttHint: 'Enlace copiado — abre TikTok y pégalo en una descripción o DM.',
    smsBody: 'Alguien escribió un anuncio vivo que vale la pena leer 💛',
    emailSubject: 'Un anuncio vivo para ti',
    emailBody: 'Encontré esto — sentí que valía la pena compartirlo contigo.',
    footer: 'Tú decides dónde se comparte.',
    brand: 'Say It NOW',
  },
  pt: {
    title: 'Compartilhe este Anúncio Vivo',
    subtitle: 'Um pequeno gesto pode mudar o dia de alguém.',
    cardTagline: 'Um anúncio vivo do coração',
    forLabel: 'Para',
    copyLink: 'Copiar link',
    text: 'Mensagem',
    email: 'E-mail',
    facebook: 'Facebook',
    instagram: 'Instagram',
    tiktok: 'TikTok',
    nativeShare: 'Compartilhar…',
    copied: 'Link copiado. Cole onde fizer sentido.',
    copyFail: 'Não foi possível copiar. Segure o link para copiar.',
    igHint: 'Link copiado — abra o Instagram e cole em um story ou DM.',
    ttHint: 'Link copiado — abra o TikTok e cole numa legenda ou DM.',
    smsBody: 'Alguém escreveu um anúncio vivo que vale a pena ler 💛',
    emailSubject: 'Um anúncio vivo para você',
    emailBody: 'Encontrei isto — senti que valia compartilhar com você.',
    footer: 'Você está no controle de onde isso é compartilhado.',
    brand: 'Say It NOW',
  },
  fr: {
    title: 'Partager cette Annonce Vivante',
    subtitle: 'Un petit partage peut changer la journée de quelqu\'un.',
    cardTagline: 'Une annonce vivante venue du cœur',
    forLabel: 'Pour',
    copyLink: 'Copier le lien',
    text: 'SMS',
    email: 'E-mail',
    facebook: 'Facebook',
    instagram: 'Instagram',
    tiktok: 'TikTok',
    nativeShare: 'Partager…',
    copied: 'Lien copié. Collez-le où vous voulez.',
    copyFail: 'Impossible de copier. Appuyez longuement sur le lien.',
    igHint: 'Lien copié — ouvrez Instagram et collez-le dans une story ou un DM.',
    ttHint: 'Lien copié — ouvrez TikTok et collez-le dans une légende ou un DM.',
    smsBody: 'Quelqu\'un a écrit une annonce vivante à lire 💛',
    emailSubject: 'Une annonce vivante pour vous',
    emailBody: 'Je suis tombé(e) sur ceci — j\'ai trouvé que ça valait la peine de le partager avec vous.',
    footer: 'Vous décidez où c\'est partagé.',
    brand: 'Say It NOW',
  },
  ar: {
    title: 'شارك هذا الإعلان الحي',
    subtitle: 'مشاركة صغيرة يمكن أن تغير يوم شخص ما.',
    cardTagline: 'إعلان حي من القلب',
    forLabel: 'إلى',
    copyLink: 'نسخ الرابط',
    text: 'رسالة',
    email: 'بريد',
    facebook: 'فيسبوك',
    instagram: 'إنستغرام',
    tiktok: 'تيك توك',
    nativeShare: 'مشاركة…',
    copied: 'تم نسخ الرابط. الصقه أينما تشاء.',
    copyFail: 'تعذر النسخ. اضغط مطولاً على الرابط لنسخه.',
    igHint: 'تم نسخ الرابط — افتح إنستغرام والصقه في قصة أو رسالة.',
    ttHint: 'تم نسخ الرابط — افتح تيك توك والصقه في وصف أو رسالة.',
    smsBody: 'كتب شخص ما إعلاناً حياً يستحق القراءة 💛',
    emailSubject: 'إعلان حي لك',
    emailBody: 'صادفت هذا — شعرت أنه يستحق المشاركة معك.',
    footer: 'أنت تتحكم بمكان المشاركة.',
    brand: 'Say It NOW',
  },
  ja: {
    title: 'このリビング・アナウンスをシェア',
    subtitle: '小さなシェアが誰かの一日を変えることがあります。',
    cardTagline: '心からのリビング・アナウンス',
    forLabel: '宛先',
    copyLink: 'リンクをコピー',
    text: 'メッセージ',
    email: 'メール',
    facebook: 'Facebook',
    instagram: 'Instagram',
    tiktok: 'TikTok',
    nativeShare: 'シェア…',
    copied: 'リンクをコピーしました。お好きな場所に貼り付けてください。',
    copyFail: 'コピーできませんでした。リンクを長押ししてください。',
    igHint: 'リンクをコピーしました — Instagramを開いてストーリーやDMに貼り付けてください。',
    ttHint: 'リンクをコピーしました — TikTokを開いてキャプションやDMに貼り付けてください。',
    smsBody: '誰かが読む価値のあるリビング・アナウンスを書きました 💛',
    emailSubject: 'あなたへのリビング・アナウンス',
    emailBody: 'これを見つけて、あなたと共有する価値があると感じました。',
    footer: 'シェアする場所はあなたが決めます。',
    brand: 'Say It NOW',
  },
  ko: {
    title: '이 리빙 어나운스먼트 공유하기',
    subtitle: '작은 공유가 누군가의 하루를 바꿀 수 있어요.',
    cardTagline: '마음에서 우러난 리빙 어나운스먼트',
    forLabel: '받는 사람',
    copyLink: '링크 복사',
    text: '문자',
    email: '이메일',
    facebook: 'Facebook',
    instagram: 'Instagram',
    tiktok: 'TikTok',
    nativeShare: '공유…',
    copied: '링크가 복사되었습니다. 원하는 곳에 붙여넣으세요.',
    copyFail: '복사할 수 없습니다. 링크를 길게 눌러 복사하세요.',
    igHint: '링크 복사됨 — Instagram을 열고 스토리나 DM에 붙여넣으세요.',
    ttHint: '링크 복사됨 — TikTok을 열고 캡션이나 DM에 붙여넣으세요.',
    smsBody: '누군가 읽을 만한 리빙 어나운스먼트를 썼어요 💛',
    emailSubject: '당신을 위한 리빙 어나운스먼트',
    emailBody: '이걸 발견했는데, 당신과 공유할 가치가 있다고 느꼈어요.',
    footer: '어디에 공유할지는 당신이 정합니다.',
    brand: 'Say It NOW',
  },
  id: {
    title: 'Bagikan Pengumuman Hidup Ini',
    subtitle: 'Sedikit berbagi bisa mengubah hari seseorang.',
    cardTagline: 'Pengumuman hidup dari hati',
    forLabel: 'Untuk',
    copyLink: 'Salin tautan',
    text: 'Pesan',
    email: 'Email',
    facebook: 'Facebook',
    instagram: 'Instagram',
    tiktok: 'TikTok',
    nativeShare: 'Bagikan…',
    copied: 'Tautan disalin. Tempelkan di mana pun terasa pas.',
    copyFail: 'Tidak bisa menyalin. Tekan lama tautan untuk menyalin.',
    igHint: 'Tautan disalin — buka Instagram dan tempelkan di story atau DM.',
    ttHint: 'Tautan disalin — buka TikTok dan tempelkan di caption atau DM.',
    smsBody: 'Seseorang menulis pengumuman hidup yang layak dibaca 💛',
    emailSubject: 'Sebuah pengumuman hidup untukmu',
    emailBody: 'Saya menemukan ini — terasa pantas untuk kubagikan denganmu.',
    footer: 'Kamu yang menentukan ke mana ini dibagikan.',
    brand: 'Say It NOW',
  },
  hi: {
    title: 'इस लिविंग अनाउंसमेंट को साझा करें',
    subtitle: 'एक छोटा सा साझा किसी का दिन बदल सकता है।',
    cardTagline: 'दिल से बनी लिविंग अनाउंसमेंट',
    forLabel: 'के लिए',
    copyLink: 'लिंक कॉपी करें',
    text: 'संदेश',
    email: 'ईमेल',
    facebook: 'Facebook',
    instagram: 'Instagram',
    tiktok: 'TikTok',
    nativeShare: 'साझा करें…',
    copied: 'लिंक कॉपी हो गया। जहाँ चाहें वहाँ पेस्ट करें।',
    copyFail: 'कॉपी नहीं हो सका। लिंक को देर तक दबाकर कॉपी करें।',
    igHint: 'लिंक कॉपी — Instagram खोलें और स्टोरी या DM में पेस्ट करें।',
    ttHint: 'लिंक कॉपी — TikTok खोलें और कैप्शन या DM में पेस्ट करें।',
    smsBody: 'किसी ने पढ़ने लायक एक लिविंग अनाउंसमेंट लिखा है 💛',
    emailSubject: 'आपके लिए एक लिविंग अनाउंसमेंट',
    emailBody: 'मुझे यह मिला — लगा कि आपके साथ साझा करने लायक है।',
    footer: 'कहाँ साझा करना है, आप तय करते हैं।',
    brand: 'Say It NOW',
  },
};

interface RecipientShareSectionProps {
  publicSlug: string;
  recipientName: string;
  relationship?: string;
  template?: string;
  senderName?: string | null;
  senderReferralCode?: string | null;
  /** Photo URLs to preload before showing share section */
  photoUrls?: string[] | null;
  /** Video URL to wait for metadata */
  videoUrl?: string | null;
  /** Source tag for analytics */
  source?: string;
}

/**
 * Preload all attachments. Resolves once images are loaded (or errored)
 * with a 4s safety timeout.
 */
function useAttachmentsReady(photoUrls?: string[] | null, videoUrl?: string | null): boolean {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const urls = (photoUrls || []).filter(Boolean);
    if (urls.length === 0 && !videoUrl) {
      setReady(true);
      return;
    }

    let cancelled = false;
    let pending = urls.length;
    const done = () => {
      if (cancelled) return;
      pending -= 1;
      if (pending <= 0) setReady(true);
    };

    urls.forEach((url) => {
      const img = new Image();
      img.onload = done;
      img.onerror = done;
      img.src = url;
    });

    // Safety timeout — never block the share UI for more than 4s.
    const t = window.setTimeout(() => {
      if (!cancelled) setReady(true);
    }, 4000);

    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [photoUrls?.join('|'), videoUrl]);

  return ready;
}

export function RecipientShareSection({
  publicSlug,
  recipientName,
  relationship,
  template,
  senderName,
  senderReferralCode,
  photoUrls,
  videoUrl,
  source = 'recipient_share_section',
}: RecipientShareSectionProps) {
  const { language } = useLanguage();
  const t = lc(copy, language);
  const { trackEvent } = useEventTracking();
  const ready = useAttachmentsReady(photoUrls, videoUrl);
  const [copied, setCopied] = useState(false);

  const cleanUrl = useMemo(() => getAnnouncementShareUrl(publicSlug), [publicSlug]);
  const links = useMemo(
    () =>
      buildShareLinks({
        publicSlug,
        templateSlug: template,
        relationship,
        userRef: senderReferralCode || undefined,
        senderName,
      }),
    [publicSlug, template, relationship, senderReferralCode, senderName],
  );

  const templateLabel = useMemo(() => {
    try {
      return template ? getTemplateConfig(template).label : '';
    } catch {
      return '';
    }
  }, [template]);

  const supportsNativeShare =
    typeof navigator !== 'undefined' && typeof (navigator as any).share === 'function';

  if (!ready) return null;

  const track = (platform: string) => {
    try {
      trackEvent('recipient_share_click', { platform, source, slug: publicSlug });
    } catch {
      // ignore
    }
  };

  const copyToClipboard = async (text: string, hint?: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(hint || t.copied);
      window.setTimeout(() => setCopied(false), 2400);
      return true;
    } catch {
      toast.error(t.copyFail);
      return false;
    }
  };

  const handleNativeShare = async () => {
    track('native');
    try {
      await (navigator as any).share({
        title: t.title,
        text: t.smsBody,
        url: cleanUrl,
      });
    } catch {
      // user cancelled — silently ignore
    }
  };

  const handleCopyLink = () => {
    track('copy_link');
    copyToClipboard(cleanUrl);
  };

  const handleSms = () => {
    track('sms');
    const body = encodeURIComponent(`${t.smsBody} ${cleanUrl}`);
    // iOS uses & separator, Android uses ?
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const sep = /android/i.test(ua) ? '?' : '&';
    window.location.href = `sms:${sep}body=${body}`;
  };

  const handleEmail = () => {
    track('email');
    const subject = encodeURIComponent(t.emailSubject);
    const body = encodeURIComponent(`${t.emailBody}\n\n${cleanUrl}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleFacebook = () => {
    track('facebook');
    window.open(links.facebook, '_blank', 'noopener,noreferrer,width=600,height=600');
  };

  const handleInstagram = async () => {
    track('instagram');
    await copyToClipboard(cleanUrl, t.igHint);
    // Try to deep-link Instagram (won't work on desktop, harmless)
    window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
  };

  const handleTikTok = async () => {
    track('tiktok');
    await copyToClipboard(cleanUrl, t.ttHint);
    window.open('https://www.tiktok.com/', '_blank', 'noopener,noreferrer');
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 0.61, 0.36, 1] }}
      className="relative z-30 px-4 sm:px-6 py-10 sm:py-12"
      aria-label={t.title}
    >
      <div className="max-w-md mx-auto space-y-6">
        {/* Branded preview card */}
        <div className="relative overflow-hidden rounded-2xl border border-border/60 bg-gradient-to-br from-card/90 via-card/70 to-card/40 backdrop-blur-md shadow-elevation-2 p-5">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground/80">
                {templateLabel ? `${templateLabel} · ${t.brand}` : t.brand}
              </p>
              <p className="text-sm font-medium text-foreground/95 mt-0.5 truncate">
                {t.forLabel} <span className="text-primary">{recipientName}</span>
              </p>
              <p className="text-xs text-muted-foreground mt-1 italic">
                {t.cardTagline}
              </p>
            </div>
          </div>
          {/* Subtle decorative glow */}
          <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-primary/10 blur-3xl" aria-hidden="true" />
        </div>

        {/* Title */}
        <div className="text-center space-y-1.5">
          <h2 className="text-lg sm:text-xl font-medium text-foreground/95">{t.title}</h2>
          <p className="text-xs sm:text-sm text-muted-foreground">{t.subtitle}</p>
        </div>

        {/* Native share (mobile-first) */}
        {supportsNativeShare && (
          <Button
            onClick={handleNativeShare}
            size="lg"
            className="w-full justify-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 shadow-md shadow-primary/20"
          >
            <Share2 className="h-4 w-4" />
            {t.nativeShare}
          </Button>
        )}

        {/* Primary actions */}
        <div className="grid grid-cols-3 gap-2.5">
          <ShareTile
            icon={copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
            label={t.copyLink}
            onClick={handleCopyLink}
            highlighted={copied}
          />
          <ShareTile
            icon={<MessageSquare className="h-5 w-5" />}
            label={t.text}
            onClick={handleSms}
          />
          <ShareTile
            icon={<Mail className="h-5 w-5" />}
            label={t.email}
            onClick={handleEmail}
          />
          <ShareTile
            icon={<Facebook className="h-5 w-5" />}
            label={t.facebook}
            onClick={handleFacebook}
          />
          <ShareTile
            icon={<Instagram className="h-5 w-5" />}
            label={t.instagram}
            onClick={handleInstagram}
          />
          <ShareTile
            icon={<TikTokIcon className="h-5 w-5" />}
            label={t.tiktok}
            onClick={handleTikTok}
          />
        </div>

        <p className="text-[11px] text-center text-muted-foreground/70">{t.footer}</p>
      </div>
    </motion.section>
  );
}

function ShareTile({
  icon,
  label,
  onClick,
  highlighted,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  highlighted?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'group flex flex-col items-center justify-center gap-1.5 rounded-xl border px-2 py-3.5 transition-all',
        'min-h-[76px] text-foreground/85 hover:text-foreground',
        highlighted
          ? 'border-primary/60 bg-primary/10'
          : 'border-border/60 bg-card/40 hover:bg-card/70 hover:border-border',
      ].join(' ')}
    >
      <span className="text-foreground/80 group-hover:text-primary transition-colors">
        {icon}
      </span>
      <span className="text-[11px] font-medium leading-none">{label}</span>
    </button>
  );
}
